import { create } from 'zustand';
import { isMac } from './platform';

/**
 * "Shortcut reveal" mode. Keyboard-shortcut hints on the top chrome controls
 * (toolbar buttons, the view switcher, document tabs, the logo) are HIDDEN by
 * default so the buttons stay compact. They appear as small top-right badges
 * only while the platform "command" modifier is held ALONE for ~400ms.
 *
 * State machine (driven by {@link installShortcutReveal}):
 *   idle ──(mod keydown, not repeat)──▶ pending (400ms timer running)
 *   pending ──(timer fires)──▶ revealed
 *   pending|revealed ──(mod keyup / any other key / blur / tab hidden)──▶ idle
 *
 * The mod key itself becoming a real chord (the user presses another key while
 * holding it) cancels reveal — a chord is a shortcut being *used*, not the
 * user asking "what are the shortcuts?". Auto-repeat (`e.repeat`) is ignored so
 * a held modifier doesn't restart the timer.
 *
 * This store carries ONLY the `revealed` boolean (global UI state). The timer
 * and listener lifecycle live in the controller so they have a single owner.
 */

/** How long the modifier must be held alone before badges appear. */
export const REVEAL_DELAY_MS = 400;

interface ShortcutRevealStore {
  revealed: boolean;
  setRevealed: (revealed: boolean) => void;
}

export const useShortcutRevealStore = create<ShortcutRevealStore>((set) => ({
  revealed: false,
  setRevealed: (revealed) =>
    // Reference-equality early-out so subscribers don't re-render on no-op sets
    // (the controller fires `setRevealed(false)` from several handlers).
    set((s) => (s.revealed === revealed ? s : { revealed })),
}));

/** Selector hook: is shortcut-reveal mode currently active? */
export function useShortcutRevealed(): boolean {
  return useShortcutRevealStore((s) => s.revealed);
}

/**
 * The key value (KeyboardEvent.key) for the platform "command" modifier:
 * `Meta` (⌘) on macOS, `Control` on Windows/Linux. Matches the modifier
 * `modPressed`/`shortcutLabel` use, so the revealed hints map to the bindings
 * that actually fire.
 */
function modKey(): 'Meta' | 'Control' {
  return isMac() ? 'Meta' : 'Control';
}

/**
 * Install the global window listeners that drive reveal mode. Call once from an
 * App-level effect; returns a cleanup that removes the listeners and clears any
 * pending timer. SSR/jsdom-safe to *call* — but the caller should still guard
 * `typeof window === 'undefined'` to avoid referencing `window` at all.
 */
export function installShortcutReveal(): () => void {
  if (typeof window === 'undefined') {
    return () => {};
  }

  const mod = modKey();
  // Single timer owner. `null` ⇔ no pending hold.
  let timer: ReturnType<typeof setTimeout> | null = null;

  const clearTimer = (): void => {
    if (timer !== null) {
      clearTimeout(timer);
      timer = null;
    }
  };

  const reset = (): void => {
    clearTimer();
    useShortcutRevealStore.getState().setRevealed(false);
  };

  const onKeyDown = (e: KeyboardEvent): void => {
    // Ignore auto-repeat — a held modifier fires keydown repeatedly; we don't
    // want it to restart the timer.
    if (e.repeat) return;

    if (e.key === mod) {
      // The modifier was pressed alone. Start the hold timer if we're not
      // already pending or revealed.
      const { revealed } = useShortcutRevealStore.getState();
      if (timer === null && !revealed) {
        timer = setTimeout(() => {
          timer = null;
          useShortcutRevealStore.getState().setRevealed(true);
        }, REVEAL_DELAY_MS);
      }
      return;
    }

    // Any OTHER key while holding (or after) the modifier means the user is
    // forming/using a chord — cancel reveal immediately.
    reset();
  };

  const onKeyUp = (e: KeyboardEvent): void => {
    // Releasing the modifier exits reveal mode (and cancels a pending hold).
    if (e.key === mod) reset();
  };

  const onBlur = (): void => reset();

  const onVisibilityChange = (): void => {
    // Tab hidden / backgrounded — never get stuck "on" while unfocused.
    if (typeof document !== 'undefined' && document.hidden) reset();
  };

  window.addEventListener('keydown', onKeyDown);
  window.addEventListener('keyup', onKeyUp);
  window.addEventListener('blur', onBlur);
  document.addEventListener('visibilitychange', onVisibilityChange);

  return () => {
    clearTimer();
    window.removeEventListener('keydown', onKeyDown);
    window.removeEventListener('keyup', onKeyUp);
    window.removeEventListener('blur', onBlur);
    document.removeEventListener('visibilitychange', onVisibilityChange);
    // Leave reveal off on teardown so a remount starts clean.
    useShortcutRevealStore.getState().setRevealed(false);
  };
}
